import { brand, type BrandId } from "./activeBrand";
import logo from "../assets/brand/logo.svg";
import logoWhite from "../assets/brand/logo-white.svg";
import logoPng from "../assets/brand/logo.png";

export { BRAND_IDS, marketingUrlFromEnv } from "./activeBrand";
export type { BrandId } from "./activeBrand";

/** Backend multi-tenant platform id — always the lowercase brand id. */
export const platform: BrandId = brand.id as BrandId;

/**
 * Brand-scoped sessionStorage key so tokens never cross brands on the same
 * origin (e.g. localhost). Different production domains are already isolated.
 */
export const ACCESS_TOKEN_KEY = `accessToken:${platform}`;

/** Pre-multi-brand OQ key — migrate only onto the ophthalmologyq brand key. */
const LEGACY_OQ_ACCESS_TOKEN_KEY = "accessTokenOq";
/** Short-lived unscoped key from earlier multi-brand work — never reuse across brands. */
const LEGACY_UNSCOPED_ACCESS_TOKEN_KEY = "accessToken";

/** Read this brand's token; migrate legacy OQ key when on ophthalmologyq only. */
export function getAccessToken(): string | null {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    return token;
  }

  if (platform === "ophthalmologyq") {
    const legacyOqToken = sessionStorage.getItem(LEGACY_OQ_ACCESS_TOKEN_KEY);
    if (legacyOqToken) {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, legacyOqToken);
      sessionStorage.removeItem(LEGACY_OQ_ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(LEGACY_UNSCOPED_ACCESS_TOKEN_KEY);
      return legacyOqToken;
    }
  }

  // Drop any unscoped token rather than attaching it to the wrong brand.
  sessionStorage.removeItem(LEGACY_UNSCOPED_ACCESS_TOKEN_KEY);
  return null;
}

export function setAccessToken(token: string): void {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  sessionStorage.removeItem(LEGACY_UNSCOPED_ACCESS_TOKEN_KEY);
  if (platform === "ophthalmologyq") {
    sessionStorage.removeItem(LEGACY_OQ_ACCESS_TOKEN_KEY);
  }
}

/** Clear this brand's token and any legacy keys that could leak across brands. */
export function clearAccessToken(): void {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_UNSCOPED_ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_OQ_ACCESS_TOKEN_KEY);
}

/**
 * True only when this brand has its own access token.
 * Auth cookies are shared on the API host across brands — never restore a
 * session from cookies alone or OQ login would authenticate GP (and vice versa).
 */
export function hasBrandSession(): boolean {
  return getAccessToken() !== null;
}

/** User-facing support inbox for the active brand (never hard-code in UI). */
export const contactEmail = brand.contactEmail;
export const contactMailto = `mailto:${brand.contactEmail}`;

export { brand };
export type { Brand } from "./activeBrand";

export const brandLogos = {
  color: logo,
  white: logoWhite,
  png: logoPng,
} as const;
